import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Plus,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertTriangle,
  ArrowUpCircle,
  Send,
  Loader2,
  Ticket,
  Filter,
} from 'lucide-react';

interface SupportTicket {
  id: string;
  ticket_number: number;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  level: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  creator?: { full_name: string };
}

interface TicketMessage {
  id: string;
  ticket_id: string;
  sender_type: string;
  sender_id: string;
  message: string;
  is_internal: boolean;
  created_at: string;
  sender?: { full_name: string };
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  open: { label: 'Abierto', color: 'bg-blue-500', icon: Clock },
  in_progress: { label: 'En Progreso', color: 'bg-amber-500', icon: Loader2 },
  resolved: { label: 'Resuelto', color: 'bg-green-500', icon: CheckCircle },
  closed: { label: 'Cerrado', color: 'bg-gray-500', icon: CheckCircle },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  low: { label: 'Baja', color: 'bg-gray-400' },
  normal: { label: 'Normal', color: 'bg-blue-400' },
  high: { label: 'Alta', color: 'bg-orange-500' },
  critical: { label: 'Crítica', color: 'bg-red-500' },
};

const CATEGORY_OPTIONS = [
  { value: 'bug', label: 'Error / Bug' },
  { value: 'feature', label: 'Nueva Funcionalidad' },
  { value: 'question', label: 'Pregunta' },
  { value: 'urgent', label: 'Urgente' },
];

export default function SupportTickets() {
  const { user, userRole } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [newMessage, setNewMessage] = useState('');

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formCategory, setFormCategory] = useState('question');
  const [formPriority, setFormPriority] = useState('normal');

  // Check if user can create tickets (admin/supervisor only)
  const canCreateTickets = userRole === 'admin' || userRole === 'supervisor';

  // Fetch tickets
  const { data: tickets, isLoading } = useQuery({
    queryKey: ['support-tickets', statusFilter],
    queryFn: async () => {
      let query = api
        .from('support_tickets')
        .select('*, creator:profiles!created_by(full_name)')
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as SupportTicket[];
    },
  });

  // Fetch messages for selected ticket
  const { data: messages } = useQuery({
    queryKey: ['ticket-messages', selectedTicket?.id],
    queryFn: async () => {
      if (!selectedTicket) return [];
      const { data, error } = await api
        .from('ticket_messages')
        .select('*, sender:profiles!sender_id(full_name)')
        .eq('ticket_id', selectedTicket.id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as TicketMessage[];
    },
    enabled: !!selectedTicket,
  });

  // Create ticket mutation
  const createTicket = useMutation({
    mutationFn: async () => {
      const { data, error } = await api
        .from('support_tickets')
        .insert({
          title: formTitle,
          description: formDescription,
          category: formCategory,
          priority: formPriority,
          created_by: user?.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({ title: 'Ticket creado', description: 'El ticket ha sido enviado' });
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      setCreateDialogOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  // Add message mutation
  const addMessage = useMutation({
    mutationFn: async () => {
      if (!selectedTicket || !newMessage.trim()) return;
      const { error } = await api
        .from('ticket_messages')
        .insert({
          ticket_id: selectedTicket.id,
          sender_type: 'magma_user',
          sender_id: user?.id,
          message: newMessage.trim(),
        });
      if (error) throw error;
    },
    onSuccess: () => {
      setNewMessage('');
      queryClient.invalidateQueries({ queryKey: ['ticket-messages', selectedTicket?.id] });
    },
  });

  // Update ticket status
  const updateStatus = useMutation({
    mutationFn: async (newStatus: string) => {
      if (!selectedTicket) return;
      const updates: any = { status: newStatus, updated_at: new Date().toISOString() };
      if (newStatus === 'resolved') {
        updates.resolved_at = new Date().toISOString();
      }
      const { error } = await api
        .from('support_tickets')
        .update(updates)
        .eq('id', selectedTicket.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Estado actualizado' });
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-messages', selectedTicket?.id] });
    },
  });

  // Escalate to Try! Company
  const escalateTicket = useMutation({
    mutationFn: async () => {
      if (!selectedTicket) return;
      const { error } = await api
        .from('support_tickets')
        .update({
          level: 2,
          escalated_at: new Date().toISOString(),
          escalated_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedTicket.id);
      if (error) throw error;

      // Add system message
      await api.from('ticket_messages').insert({
        ticket_id: selectedTicket.id,
        sender_type: 'system',
        message: 'Ticket escalado a Try! Company para soporte de segundo nivel.',
      });
    },
    onSuccess: () => {
      toast({ title: 'Ticket escalado', description: 'Try! Company ha sido notificado' });
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-messages', selectedTicket?.id] });
    },
  });

  const resetForm = () => {
    setFormTitle('');
    setFormDescription('');
    setFormCategory('question');
    setFormPriority('normal');
  };

  const openTicketDetail = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setDetailDialogOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Ticket className="w-7 h-7" />
              Tickets de Soporte
            </h1>
            <p className="text-muted-foreground">
              Gestión de incidencias y solicitudes
            </p>
          </div>
          {canCreateTickets && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Ticket
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="open">Abiertos</SelectItem>
              <SelectItem value="in_progress">En Progreso</SelectItem>
              <SelectItem value="resolved">Resueltos</SelectItem>
              <SelectItem value="closed">Cerrados</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tickets Table */}
        <Card>
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="text-center py-8">Cargando tickets...</div>
            ) : !tickets?.length ? (
              <div className="text-center py-12">
                <Ticket className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg text-muted-foreground">No hay tickets</p>
                {canCreateTickets && (
                  <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Crear primer ticket
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Título</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Prioridad</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Nivel</TableHead>
                    <TableHead>Creado</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map((ticket) => {
                    const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
                    const priority = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.normal;
                    const StatusIcon = status.icon;

                    return (
                      <TableRow key={ticket.id} className="cursor-pointer hover:bg-muted/50">
                        <TableCell className="font-mono">#{ticket.ticket_number}</TableCell>
                        <TableCell className="font-medium max-w-xs truncate">
                          {ticket.title}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {CATEGORY_OPTIONS.find(c => c.value === ticket.category)?.label || ticket.category}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={priority.color + ' text-white'}>
                            {priority.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={status.color + ' text-white'}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {ticket.level === 2 ? (
                            <Badge variant="destructive">
                              <ArrowUpCircle className="w-3 h-3 mr-1" />
                              Escalado
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Interno</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {format(new Date(ticket.created_at), 'dd/MM/yy HH:mm', { locale: es })}
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" onClick={() => openTicketDetail(ticket)}>
                            <MessageSquare className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Ticket Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuevo Ticket de Soporte</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Título</label>
              <Input
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="Describe brevemente el problema"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Categoría</label>
                <Select value={formCategory} onValueChange={setFormCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Prioridad</label>
                <Select value={formPriority} onValueChange={setFormPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baja</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="critical">Crítica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Descripción</label>
              <Textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Describe el problema en detalle..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => createTicket.mutate()}
              disabled={!formTitle.trim() || createTicket.isPending}
            >
              {createTicket.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Crear Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          {selectedTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="font-mono text-muted-foreground">
                    #{selectedTicket.ticket_number}
                  </span>
                  {selectedTicket.title}
                </DialogTitle>
              </DialogHeader>

              <div className="flex gap-2 mb-4">
                <Badge className={STATUS_CONFIG[selectedTicket.status]?.color + ' text-white'}>
                  {STATUS_CONFIG[selectedTicket.status]?.label}
                </Badge>
                <Badge className={PRIORITY_CONFIG[selectedTicket.priority]?.color + ' text-white'}>
                  {PRIORITY_CONFIG[selectedTicket.priority]?.label}
                </Badge>
                {selectedTicket.level === 2 && (
                  <Badge variant="destructive">
                    <ArrowUpCircle className="w-3 h-3 mr-1" />
                    Escalado a Try! Company
                  </Badge>
                )}
              </div>

              {selectedTicket.description && (
                <Card className="mb-4">
                  <CardContent className="pt-4">
                    <p className="text-sm whitespace-pre-wrap">{selectedTicket.description}</p>
                  </CardContent>
                </Card>
              )}

              {/* Messages */}
              <ScrollArea className="h-64 border rounded-lg p-4 mb-4">
                {messages?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No hay mensajes aún
                  </p>
                ) : (
                  <div className="space-y-3">
                    {messages?.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-3 rounded-lg ${
                          msg.sender_type === 'system'
                            ? 'bg-muted text-center text-sm'
                            : msg.sender_id === user?.id
                            ? 'bg-primary/10 ml-8'
                            : 'bg-muted mr-8'
                        }`}
                      >
                        {msg.sender_type !== 'system' && (
                          <p className="text-xs text-muted-foreground mb-1">
                            {msg.sender?.full_name || 'Usuario'} - {format(new Date(msg.created_at), 'dd/MM HH:mm')}
                          </p>
                        )}
                        <p className="text-sm">{msg.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>

              {/* New message input */}
              {selectedTicket.status !== 'closed' && (
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Escribe un mensaje..."
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && addMessage.mutate()}
                  />
                  <Button onClick={() => addMessage.mutate()} disabled={!newMessage.trim()}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between mt-4 pt-4 border-t">
                <div className="flex gap-2">
                  {selectedTicket.status === 'open' && (
                    <Button variant="outline" size="sm" onClick={() => updateStatus.mutate('in_progress')}>
                      Marcar en progreso
                    </Button>
                  )}
                  {selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
                    <Button variant="outline" size="sm" onClick={() => updateStatus.mutate('resolved')}>
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Resolver
                    </Button>
                  )}
                </div>
                {selectedTicket.level === 1 && selectedTicket.status !== 'closed' && userRole === 'admin' && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => escalateTicket.mutate()}
                  >
                    <ArrowUpCircle className="w-4 h-4 mr-1" />
                    Escalar a Try! Company
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
